/**
 * ``/workspace/templates`` route group — Brand & Voice template editor.
 *
 * Parent layout mounts a SurfaceLayout with a header, the kind-tabs strip
 * (Chrome / Paper / Voice), and an Outlet that hosts the per-kind editor.
 * Mirrors the pattern used by ``/workspace/integrations`` so the chrome
 * stays uniform across workspace surfaces.
 *
 * The route is reachable via the StudioDrawer's "Modifica template" CTA;
 * it intentionally does NOT register in ``WORKSPACE_TAB_DEFS`` because the
 * six top tabs are a fixed v18-mockup contract (per CLAUDE.md "Phase 8"
 * convention — same pattern as canvases, shared, dxf-library).
 */

import { Link, Outlet, createFileRoute, useParams } from "@tanstack/react-router";
import { Image as ImageIcon, Mic, Palette } from "lucide-react";
import type * as React from "react";

import type { TemplateKind } from "@ando/core";
import { SurfaceHeader, SurfaceLayout, cn } from "@ando/ui";

import { validateWorkspaceSearch } from "@/features/workspace-router";

export const Route = createFileRoute("/_authed/workspace/templates")({
  validateSearch: validateWorkspaceSearch,
  component: TemplatesShell,
});

interface TabDef {
  readonly kind: TemplateKind;
  readonly label: string;
  readonly icon: React.ComponentType<{ size?: number; className?: string }>;
}

const TABS: ReadonlyArray<TabDef> = [
  { kind: "chrome", label: "Chrome", icon: Palette },
  { kind: "paper", label: "Paper", icon: ImageIcon },
  { kind: "writing_style", label: "Voice", icon: Mic },
];

function TemplatesShell() {
  // The active kind lives in the URL — read it via the param hook (the
  // sub-route ``$kind.tsx`` declares it). For the index redirect the param
  // is undefined; we tolerate that and don't highlight any tab.
  const params = useParams({ strict: false }) as { kind?: string };
  const activeKind = params.kind as TemplateKind | undefined;

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          title="Brand & Voice templates"
          meta={
            <span className="text-ink-3">
              Personalizza l'aspetto di slide deck, documenti e tono di voce.
            </span>
          }
        />
      }
      toolbar={
        <div className="flex items-center gap-1 px-8 pb-3">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = activeKind === t.kind;
            return (
              <Link
                key={t.kind}
                to="/workspace/templates/$kind"
                params={{ kind: t.kind }}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 font-medium text-[11.5px] transition-colors",
                  isActive
                    ? "bg-ink-1 text-white"
                    : "bg-transparent text-ink-2 hover:bg-white/60 hover:text-ink-1",
                )}
              >
                <Icon size={12} aria-hidden />
                {t.label}
              </Link>
            );
          })}
        </div>
      }
      contentAriaLabel="Editor di template per slide e documenti"
    >
      <Outlet />
    </SurfaceLayout>
  );
}
